import React, { useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ValidationMessages, { ValidationProblem } from './ValidationMessages';
import { codeOf, formatValue, OntologyKind } from './ontologyFormat';
import { dsVar, ink } from '../../theme/tokens';

interface ApproveDialogProps {
    open: boolean;
    kind: OntologyKind;
    /** The draft being approved. */
    document: Record<string, any> | null;
    /** The approved version this will replace, if any — for the before/after comparison. */
    currentInForce?: Record<string, any> | null;
    warnings?: ValidationProblem[];
    submitting?: boolean;
    onClose: () => void;
    onConfirm: (effectiveFrom: string | null) => void;
}

const labelSx = {
    fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.05em',
    textTransform: 'uppercase' as const, color: dsVar('fg-muted'), mb: 0.5,
};

/**
 * Approval, with the consequence stated before the user commits.
 *
 * Approving a PARAMETER is not a metadata edit. The engine reads only approved, in-force
 * documents, so within one cache TTL the thresholds the detection nodes apply will
 * change — this is a change to what the institution alerts on. Saying so, and showing the
 * current value beside the proposed one, is the whole point of this dialog; if that were
 * cut, the button should be cut with it.
 */
const ApproveDialog: React.FC<ApproveDialogProps> = ({
    open, kind, document, currentInForce, warnings = [], submitting, onClose, onConfirm,
}) => {
    const [effectiveFrom, setEffectiveFrom] = useState('');

    if (!document) return null;

    const isParameter = kind === 'parameters';
    // By kind. This chain happened to be ordered correctly, but the same shape read the
    // wrong field off a parameter row in the browse table — see codeOf.
    const code = codeOf(document, kind);

    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, backgroundImage: 'none' } }}
        >
            <DialogTitle sx={{ color: dsVar('fg'), fontSize: '1rem', fontWeight: 600 }}>
                Approve this {isParameter ? 'threshold' : 'document'}?
            </DialogTitle>

            <DialogContent>
                {isParameter && (
                    <Box
                        sx={{
                            display: 'flex', gap: 1.25, alignItems: 'flex-start',
                            p: 1.5, mb: 2.5, borderRadius: '6px',
                            backgroundColor: 'rgba(251, 191, 36, 0.08)',
                            border: '1px solid rgba(251, 191, 36, 0.35)',
                        }}
                    >
                        <WarningAmberIcon sx={{ fontSize: 20, color: ink('#FBBF24'), mt: '1px', flexShrink: 0 }} />
                        <Box>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: ink('#FCD34D'), mb: 0.5 }}>
                                This changes what the institution alerts on.
                            </Typography>
                            <Typography sx={{ fontSize: '0.8125rem', color: ink('#FCD34D'), lineHeight: 1.6 }}>
                                Once approved, the detection nodes will apply this value on their next
                                run. This is not a metadata edit.
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* The before/after. An approver deciding without it is guessing. */}
                {isParameter && (
                    <Box sx={{ display: 'flex', gap: 3, mb: 2.5 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={labelSx}>Currently in force</Typography>
                            <Typography sx={{ fontSize: '1.125rem', color: dsVar('fg-2'), fontWeight: 600 }}>
                                {currentInForce
                                    ? formatValue(currentInForce.value, currentInForce.unit)
                                    : 'Nothing yet'}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={labelSx}>Proposed</Typography>
                            <Typography sx={{ fontSize: '1.125rem', color: dsVar('accent-fg'), fontWeight: 600 }}>
                                {formatValue(document.value, document.unit)}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {document.proposal_note && (
                    <Box sx={{ mb: 2.5 }}>
                        <Typography sx={labelSx}>Proposal note</Typography>
                        <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), lineHeight: 1.6 }}>
                            {document.proposal_note}
                        </Typography>
                    </Box>
                )}

                {/* Where "your threshold is looser than statute" appears. */}
                <ValidationMessages warnings={warnings} />

                <Box sx={{ mb: 1 }}>
                    <Typography sx={labelSx}>Effective from</Typography>
                    <TextField
                        type="date"
                        size="small"
                        fullWidth
                        value={effectiveFrom}
                        onChange={(e) => setEffectiveFrom(e.target.value)}
                        helperText="Leave blank to take effect immediately."
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: dsVar('page'), color: dsVar('fg-body'), fontSize: '0.875rem',
                                '& fieldset': { borderColor: dsVar('line') },
                                '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') },
                            },
                            '& .MuiFormHelperText-root': { color: dsVar('fg-muted'), fontSize: '0.75rem' },
                        }}
                    />
                </Box>

                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), lineHeight: 1.6 }}>
                    You will be recorded as the approver of{' '}
                    <Typography component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem' }}>
                        {code}
                    </Typography>.
                </Typography>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={submitting} sx={{ color: dsVar('fg-2'), textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    onClick={() => onConfirm(effectiveFrom || null)}
                    disabled={submitting}
                    sx={{
                        textTransform: 'none', fontWeight: 600,
                        backgroundColor: dsVar('accent'), color: dsVar('on-accent'),
                        '&:hover': { backgroundColor: '#F5D149' },
                        '&.Mui-disabled': { backgroundColor: dsVar('line'), color: dsVar('fg-muted') },
                    }}
                >
                    {submitting ? 'Approving…' : 'Approve'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ApproveDialog;
